import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { actifTypes } from '@lib/components/investisseur-details/tabs/connaissance-client/steps/patrimoine-step/models/patrimoine-form.model';
import { PatrimoineService } from '@lib/components/investisseur-details/tabs/connaissance-client/steps/patrimoine-step/patrimoine.service';
import { ObjectifsService } from '@lib/components/investisseur-details/tabs/connaissance-client/steps/recueil-informations/recueil-objectifs/recueil-objectifs.service';
import { AuthService } from '@lib/services/auth-service.service';
import { EtiquetteService } from '@lib/services/etiquette.service';
import { ReferenceDataService } from '@lib/services/reference-data.service';
import { OuiSelectOption } from 'omnium-ui/form-field';
import { map, Observable } from 'rxjs';
import { buildTypeOptions } from '../filtre-investisseur-helper';
import { BuilderCode, FiltreBuilder } from './filtre-builder.model';
import { getActifsBuilders } from './themes/actifs-builders';
import { getButtonBuilders } from './themes/button-builders';
import { getContratsBuilders } from './themes/contrats-builders';
import { getCoordonneesBuilders } from './themes/coordonnees-builders';
import { getFiscaliteBuilders } from './themes/fiscalite-builders';
import { getIdentiteBuilders } from './themes/identite-builders';
import { getImmoBuilders } from './themes/immo-builders';
import { getObjectifBuilders } from './themes/objectif-builders';
import { getPassifsBuilders } from './themes/passifs-builders';
import { getProfessionnelBuilders } from './themes/professionnel-builders';
import { getProfilInvestisseurBuilders } from './themes/profil-investisseur-builders';
import { getReglementaireBuilders } from './themes/reglementaire-builders';
import { getRelationsBuilders } from './themes/relations-builders';
import { getRevenusChargesBuilders } from './themes/revenus-charges-builders';
import { getRgpdBuilders } from './themes/rgpd-builders';
import {
  getLevierCoupleBuilders,
  getLevierInvestisseursBuilders,
} from '@lib/services/filtrer-investisseurs/filtre-builders/themes/levier-builder';

@Injectable({
  providedIn: 'root',
})
export class FiltreBuilderService {
  private readonly referenceDataService = inject(ReferenceDataService);
  private readonly patrimoineService = inject(PatrimoineService);
  private readonly etiquetteService = inject(EtiquetteService);
  private readonly authService = inject(AuthService);
  private readonly ID_CSP_AUTRE_SANS_PROFESSION = 8;
  private readonly cspTopOptions$ = this.referenceDataService.getCategorieSocioProfessionnelles().pipe(
    map(options => {
      if (!options) return null;
      const topLevelOptions = options.filter(o => o.data?.parent === null);
      // "Autre sans profession" à la fin
      return [...topLevelOptions].sort((a, b) => {
        if (a.value === this.ID_CSP_AUTRE_SANS_PROFESSION) return 1;
        if (b.value === this.ID_CSP_AUTRE_SANS_PROFESSION) return -1;

        // et tri alphabétique
        return a.label.localeCompare(b.label);
      });
    })
  );

  private categoriePatrimoine$ = toObservable(this.patrimoineService.patrimoineCategorieReference);
  private optionsActifFiltrePatrimoine$: Observable<OuiSelectOption<string>[]> = this.categoriePatrimoine$.pipe(
    map(categories => buildTypeOptions(actifTypes, categories))
  );

  private objectifsService = inject(ObjectifsService);
  private categoriesRef$ = toObservable(this.objectifsService.objectifCategoriesRef);
  private optionsObjectifs$: Observable<OuiSelectOption<string>[]> = this.categoriesRef$.pipe(
    map(categories => {
      return categories.map(categorie => {
        return {
          label: categorie.libelle,
          // value: categorie.code,
          // les objectifs sont les children
          children: categorie.objectifs.map(objectif => ({
            label: objectif.libelle,
            value: objectif.code,
          })),
        } satisfies OuiSelectOption<string>;
      });
    })
  );

  private readonly buttonBuilders = getButtonBuilders(this.referenceDataService, this.etiquetteService, this.authService);
  private readonly coordonneesBuilders = getCoordonneesBuilders(this.referenceDataService);
  private readonly fiscaliteBuilders = getFiscaliteBuilders(this.referenceDataService);
  private readonly rgpdBuilders = getRgpdBuilders();
  private readonly immoBuilders = getImmoBuilders(this.patrimoineService);
  private readonly actifsBuilders = getActifsBuilders(this.optionsActifFiltrePatrimoine$);
  private readonly contratsBuilders = getContratsBuilders();
  private readonly passifsBuilders = getPassifsBuilders();
  private readonly revenusChargesBuilders = getRevenusChargesBuilders();
  private readonly profilInvestisseurBuilders = getProfilInvestisseurBuilders();
  private readonly professionnelBuilders = getProfessionnelBuilders(this.cspTopOptions$);
  private readonly reglementaireBuilders = getReglementaireBuilders();
  private readonly relationsBuilders = getRelationsBuilders(this.referenceDataService);
  private readonly objectifBuilders = getObjectifBuilders(this.optionsObjectifs$);
  private readonly identiteBuilders = getIdentiteBuilders();
  private readonly levierInvestisseursBuilders = getLevierInvestisseursBuilders();
  private readonly levierCoupleBuilders = getLevierCoupleBuilders();

  private readonly allBuilders: FiltreBuilder[] = [
    ...this.buttonBuilders,
    ...this.actifsBuilders,
    ...this.contratsBuilders,
    ...this.coordonneesBuilders,
    ...this.identiteBuilders,
    // ...this.immoBuilders, les builders immo ne sont plus implémentés par commentaire du ticket https://omnium-finance.visualstudio.com/Minotaure/_workitems/edit/26263
    ...this.fiscaliteBuilders,
    ...this.objectifBuilders,
    ...this.passifsBuilders,
    ...this.profilInvestisseurBuilders,
    ...this.professionnelBuilders,
    ...this.reglementaireBuilders,
    ...this.relationsBuilders,
    ...this.revenusChargesBuilders,
    ...this.levierInvestisseursBuilders,
    ...this.levierCoupleBuilders,
    ...this.rgpdBuilders,
  ];

  findBuilderByItemCode(itemCode: BuilderCode): FiltreBuilder | undefined {
    return this.allBuilders.find(b => b.getBuilderCode() === itemCode);
  }

  getButtonBuilders(): FiltreBuilder[] {
    return this.allBuilders.filter(b => b.isButton);
  }

  getSelectableCustomBuilders(): FiltreBuilder[] {
    return this.allBuilders.filter(b => !b.isButton);
  }
}
